
import { Component, Prop, Vue } from 'vue-property-decorator';
import { ValidationProvider, ValidationObserver } from 'vee-validate';
import { Action, Getter } from 'vuex-class';
import { Validate } from '@/types';
import InfoBanner from '@/components/shared/InfoBanner.vue';
import LoadingSpinner from '@/components/shared/LoadingSpinner.vue';
import Modal from '@/components/shared/Modal.vue';
import StudentFormFields from '@/components/profile/students/StudentFormFields.vue';
import '@/validation-rules';

const namespace = 'restaurant';

@Component<SelectStudentModal>({
	components: {
		InfoBanner,
		LoadingSpinner,
		Modal,
		StudentFormFields,
		ValidationProvider,
		ValidationObserver
	}
})
export default class SelectStudentModal extends Vue {
	@Prop({ type: Boolean, required: true }) private showModal!: boolean;
	@Prop({ type: Boolean, default: false }) private isEditMode!: boolean;
	@Prop({ type: String, default: 'select' }) private initialScreen!: 'select' | 'create';
	@Action('updateDeliveryInformation', { namespace: 'cart' }) private updateDeliveryInformation!: (delivery: CheckoutDeliveryInfo) => void;
	@Action('addStudent', { namespace: 'auth' }) private addStudent!: (payload: UserStudent) => Promise<void>;
	@Action('fetchGroupLocations', { namespace }) private fetchGroupLocations!: (restaurantId: string) => Promise<GroupLocation[]>;
	@Getter('getRestaurant', { namespace }) private restaurant!: Restaurant;
	@Getter('getRestaurantGroupLocations', { namespace }) private groupLocations!: GroupLocation[];
	@Getter('getUser', { namespace: 'auth' }) private user!: UserInfo;
	@Getter('getUserStudents', { namespace: 'auth' }) private userStudents!: UserStudent[];
	@Getter('getDeliveryInformation', { namespace: 'cart' }) private deliveryInformation!: CheckoutDeliveryInfo | null;

	$refs!: {
		observer: Validate;
		'student-select': any;
	};

	private loading: boolean = false;
	private currentScreen: 'select' | 'create' = this.initialScreen;
	private selectedStudent: string = '';
	private selectedLocationDeliveryInfo: SchoolDelivery | null = null;
	private newStudent: Partial<UserStudent> = {
		name: '',
		school: '',
		grade: '',
		classroom: '',
		teacher: '',
		extra: '',
		type: 'school',
		restaurantId: '',
		locationSlug: ''
	};

	/**
	 * Get student names for dropdown options
	 *
	 * @return {string[]}
	 */
	private get studentOptions(): string[] {
		return this.userStudents?.map(student => student.name || '') || [];
	}

	/**
	 * Get the selected student data
	 *
	 * @return {UserStudent | undefined}
	 */
	private get selectedStudentData(): UserStudent | undefined {
		return this.userStudents?.find(student => student.name === this.selectedStudent);
	}

	/**
	 * Check if selected student is from a different location
	 * Shows banner for both existing students and newly created students
	 *
	 * @return {boolean}
	 */
	private get showLocationMismatchBanner(): boolean {
		// For existing students
		if (this.selectedStudentData && this.currentScreen !== 'create') {
			return this.selectedStudentData.restaurantId !== this.restaurant.app8_restaurant;
		}

		// For new students being created
		if (this.currentScreen === 'create' && this.newStudent.restaurantId) {
			return this.newStudent.restaurantId !== this.restaurant.app8_restaurant;
		}

		return false;
	}

	/**
	 * Get the destination restaurant name for the selected student or new student being created
	 *
	 * @return {string}
	 */
	private get destinationRestaurantName(): string {
		// For existing students
		if (this.selectedStudentData && this.currentScreen !== 'create') {
			const destinationLocation: GroupLocation | undefined = this.groupLocations.find(
				loc => loc.app8_restaurant === this.selectedStudentData?.restaurantId
			);
			return destinationLocation?.name || '';
		}

		// For new students being created
		if (this.currentScreen === 'create' && this.newStudent.restaurantId) {
			const destinationLocation: GroupLocation | undefined = this.groupLocations.find(
				loc => loc.app8_restaurant === this.newStudent.restaurantId
			);
			return destinationLocation?.name || '';
		}

		return '';
	}

	/**
	 * Load group locations and pre-populate student in edit mode
	 *
	 * @return {Promise<void>}
	 */
	private async created(): Promise<void> {
		// Load locations (will include current location even if no group)
		if (!this.groupLocations.length) {
			await this.loadGroupLocations();
		}

		// Pre-populate selected student when in edit mode
		if (this.isEditMode && this.deliveryInformation?.student) {
			this.selectedStudent = this.deliveryInformation.student;
		}
	}

	/**
	 * Add modal-open class to prevent page scrolling
	 *
	 * @return {void}
	 */
	private mounted(): void {
		document.documentElement.classList.add('modal-open');
	}

	/**
	 * Remove modal-open class when modal is destroyed
	 *
	 * @return {void}
	 */
	private beforeDestroy(): void {
		document.documentElement.classList.remove('modal-open');
	}

	/**
	 * Load locations from the restaurant group
	 *
	 * @return {Promise<void>}
	 */
	private async loadGroupLocations(): Promise<void> {
		try {
			this.loading = true;
			await this.fetchGroupLocations(this.restaurant.id);
		}
		catch (error) {
			this.$toasted.error(this.$t('profile.students.select_modal.error_loading_locations'), { type: 'error', position: 'top-center' }).goAway(5000);
		}
		finally {
			this.loading = false;
		}
	}

	/**
	 * Handle student selection change
	 *
	 * @return {void}
	 */
	private onStudentChange(): void {
		this.selectedLocationDeliveryInfo = null;
		this.$nextTick(() => this.$refs['student-select']?.searchEl?.blur());
	}

	/**
	 * Switch to the create new student screen
	 *
	 * @return {void}
	 */
	private goToCreateScreen(): void {
		this.newStudent = {
			name: '',
			school: '',
			grade: '',
			classroom: '',
			teacher: '',
			extra: '',
			type: 'school',
			restaurantId: '',
			locationSlug: ''
		};
		this.currentScreen = 'create';
	}

	/**
	 * Switch back to the select student screen
	 *
	 * @return {void}
	 */
	private goToSelectScreen(): void {
		if (this.isEditMode && this.deliveryInformation?.student) {
			this.selectedStudent = this.deliveryInformation.student;
		}
		this.currentScreen = 'select';
	}

	/**
	 * Handle location (restaurant) change
	 * Updates location-specific data; field resets and auto-selection handled by StudentFormFields
	 *
	 * @param {string} restaurantId
	 * @return {void}
	 */
	private onLocationChange(restaurantId: string): void {
		const selectedLocation = this.groupLocations.find(loc => loc.app8_restaurant === restaurantId);
		if (selectedLocation) {
			this.$set(this.newStudent, 'restaurantId', restaurantId);
			this.$set(this.newStudent, 'locationSlug', selectedLocation.slug);
			this.selectedLocationDeliveryInfo = selectedLocation.delivery || null;
		}
	}

	/**
	 * Handle continue button click
	 *
	 * @return {Promise<void>}
	 */
	private async handleContinue(): Promise<void> {
		const isValid: boolean = await this.$refs.observer.validate();
		if (!isValid) {
			return;
		}
		this.currentScreen === 'create' ? await this.createStudentAndContinue() : await this.selectExistingStudentAndContinue();
	}

	/**
	 * Create new student and continue
	 *
	 * @return {Promise<void>}
	 */
	private async createStudentAndContinue(): Promise<void> {
		try {
			this.loading = true;

			// Create the student
			const studentPayload: UserStudent = {
				userId: this.user.id,
				token: this.user.token,
				name: this.newStudent.name,
				type: 'school',
				school: this.newStudent.school ?? '',
				grade: this.newStudent.grade ?? '',
				classroom: this.newStudent.classroom ?? '',
				teacher: this.newStudent.teacher ?? '',
				extra: this.newStudent.extra ?? '',
				restaurantId: this.newStudent.restaurantId,
				locationSlug: this.newStudent.locationSlug
			};

			await this.addStudent(studentPayload);

			// Check if we need to redirect to a different location, otherwise fill checkout delivery info
			if (this.newStudent.locationSlug !== this.restaurant.slug) {
				this.redirectToLocation(studentPayload);
			}
			else if (this.initialScreen === 'create') {
				this.$emit('student-created', studentPayload.name);
				this.closeModal();
			}
			else {
				this.fillCheckoutDeliveryInfo(studentPayload);
				this.closeModal();
			}
		}
		catch (error) {
			this.$toasted.error(this.$t('profile.students.error_save_student'), { type: 'error', position: 'top-center' }).goAway(5000);
		}
		finally {
			this.loading = false;
		}
	}

	/**
	 * Select existing student and continue
	 *
	 * @return {Promise<void>}
	 */
	private async selectExistingStudentAndContinue(): Promise<void> {
		if (!this.selectedStudentData) {
			return;
		}

		// Check if we need to redirect to a different location, otherwise fill checkout delivery info
		if (this.showLocationMismatchBanner) {
			this.redirectToLocation(this.selectedStudentData);
		}
		else {
			this.fillCheckoutDeliveryInfo(this.selectedStudentData);
			this.closeModal();
		}
	}

	/**
	 * Fill checkout delivery information with student data
	 *
	 * @param {UserStudent} student
	 * @return {void}
	 */
	private fillCheckoutDeliveryInfo(student: UserStudent): void {
		const deliveryInfo: CheckoutDeliveryInfo = {
			type: 'school',
			name: student.name,
			student: student.name,
			school: student.school,
			grade: student.grade,
			classroom: student.classroom,
			teacher: student.teacher,
			extra: student.extra
		};

		this.updateDeliveryInformation(deliveryInfo);
	}

	/**
	 * Redirect to a different location and save student data to session storage
	 *
	 * @param {UserStudent} student
	 * @return {void}
	 */
	private redirectToLocation(student: UserStudent): void {
		// Save student data to session storage
		sessionStorage.setItem('selectedStudentForDelivery', JSON.stringify(student));

		// Build redirect URL with current query params
		const currentQuery: Record<string, string | (string | null)[]> = this.$route.query;
		const queryParams: URLSearchParams = new URLSearchParams();

		// Preserve selectedWeek if present
		if (currentQuery.selectedWeek) {
			queryParams.append('selectedWeek', currentQuery.selectedWeek as string);
		}

		// Add any other query params (future proofing)
		Object.keys(currentQuery).forEach(key => {
			if (key !== 'selectedWeek' && currentQuery[key]) {
				queryParams.append(key, currentQuery[key] as string);
			}
		});

		const queryString: string = queryParams.toString();
		const url: string = `/${student.locationSlug}${queryString ? '?' + queryString : ''}`;

		window.location.href = url;
	}

	/**
	 * Close the modal
	 *
	 * @return {void}
	 */
	private closeModal(): void {
		this.$emit('close');
	}
}
